import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Avatar,
  TextField,
  IconButton,
  Divider,
  Paper,
  Drawer,
  useMediaQuery,
  CircularProgress,
  Popover,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import TagRoundedIcon from "@mui/icons-material/TagRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import Badge from "@mui/material/Badge";
import Tooltip from "@mui/material/Tooltip";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import useAdaptivePolling from "../../hooks/useAdaptivePolling";


import { getChannels, getChannelMessages, sendChannelMessage, deleteChannelMessage, getTeamChatMembers } from "../../api/teamchat";
import { useGlobalCounts } from "../../context/GlobalCountContext";
import useAuth from "../../hooks/useAuth";
import { useAuth as useAuthContext } from "../../context/AuthContext";
import API from "../../utils/api";
import ConfirmDialog from "../ConfirmDialog";
import { toast } from "react-toastify";
import { getUserInitials } from "../../utils/userInitials";
import { enablePushFromUserTap } from "../PushNotificationPrompt";
import {
  CHAT_BG,
  CHAT_BLUE,
  classifyTeamChatChannels,
  channelIsUnread,
  SectionLabel,
  NavRow,
  PersonRow,
} from "./teamChatChrome";

const APP_HEADER_HEIGHT = 64;

function formatMessageTime(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function getDisplayName(u) {
  if (!u) return "";
  const full = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
  return full || u.username || "";
}

function getApiErrorDetail(err, fallback = "Unknown error") {
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (typeof data?.detail === "string" && data.detail.trim()) return data.detail.trim();
  if (typeof data?.error === "string" && data.error.trim()) return data.error.trim();
  if (Array.isArray(data?.non_field_errors) && data.non_field_errors.length > 0) {
    return String(data.non_field_errors[0]);
  }
  return err?.message || fallback;
}

const AVAILABILITY_WINDOW_MS = 15 * 60 * 1000; // 15 min

function isAvailable(user) {
  const last = user?.last_login;
  if (!last) return false;
  const t = typeof last === "string" ? new Date(last).getTime() : last;
  return Date.now() - t < AVAILABILITY_WINDOW_MS;
}

/* ----------------------------- QUICK PANEL ----------------------------- */

const EMOJIS = [
  "😀", "😂", "🤣", "😊", "😍",
  "👍", "👎", "🔥", "👏", "🙏",
  "🚀", "👀", "💯", "⚡", "🎯",
  "❤️", "🤑", "💥", "🧠", "😎",
  "💲", "💰", "💵", "💸", 
];

const QUICK_REPLIES = [
  "On it 👍",
  "Sounds good!",
  "Give me a minute ⏱️",
  "Checking now 👀",
  "Let’s do it 🚀",
  "Let's Go! 💰",
];

const CHANNEL_DESCRIPTIONS = {
  general: "Company-wide space for announcements, updates, questions, and requests.",
  sales: "Dedicated space for sales reps to share wins, strategies, objections, opportunities, and field insights.",
  support: "Private, territory-based help for questions, and real-time assistance from the support team.",
};

const CHANNEL_TITLES = {
  general: "General Chat",
  sales: "Sales Chat",
  support: "Support Chat",
};

/* ----------------------------- COMPONENT ----------------------------- */

export default function TeamChatPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"), {
    noSsr: true,
    defaultMatches: typeof window !== "undefined" ? window.innerWidth < theme.breakpoints.values.md : false,
  });

  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [members, setMembers] = useState([]);
  const [input, setInput] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mentionIds, setMentionIds] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [showQuickPanel, setShowQuickPanel] = useState(false);
  const [replyContext, setReplyContext] = useState(null);
  const [sending, setSending] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [confirmDeletePost, setConfirmDeletePost] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [lastReadByChannel, setLastReadByChannel] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("teamchat_last_read") || "{}");
    } catch {
      return {};
    }
  });
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [homeSearch, setHomeSearch] = useState("");
  const [teammatePickerAnchor, setTeammatePickerAnchor] = useState(null);

  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const pollingChannelIdRef = useRef(null);
  const pollSeqRef = useRef(0);
  const hasLoadedOnceRef = useRef(false);
  const [messagesReady, setMessagesReady] = useState(false);
  const { refreshCounts } = useGlobalCounts();
  const { user: currentUser } = useAuth();
  const { isAuthenticated, bootChecked } = useAuthContext();

  /* ----------------------------- Mark mentions read on view (clear sidebar badge) ----------------------------- */
  useEffect(() => {
    let delayId = null;
    const markMentionsRead = async () => {
      try {
        await API.post("notifications/mark-mentions-read/");
        refreshCounts();
        // Delayed refetch so if refreshCounts no-oped (auth not ready), we retry once
        delayId = setTimeout(() => refreshCounts(), 300);
      } catch (err) {
        console.error("Mark mentions read failed:", err);
      }
    };
    markMentionsRead();
    return () => {
      if (delayId) clearTimeout(delayId);
    };
  }, [refreshCounts]);

  /* ----------------------------- Channels (GET) ----------------------------- */
  useEffect(() => {
    if (!bootChecked || !isAuthenticated) return;
    let cancelled = false;
    setLoadingChannels(true);
    getChannels()
      .then((res) => {
        const list = res?.data ?? [];
        if (!cancelled) {
          setChannels(list);
          if (list.length > 0 && !selectedChannel && !isMobile) setSelectedChannel(list[0]);
        }
      })
      .catch((err) => {
        if (!cancelled) setChannels([]);
        const detail = getApiErrorDetail(err, "Unable to load channels.");
        console.error("[TeamChat] channels load failed", {
          status: err?.response?.status,
          detail,
          data: err?.response?.data,
        });
        toast.error(`Team Chat channels failed to load: ${detail}`);
      })
      .finally(() => { if (!cancelled) setLoadingChannels(false); });
    return () => { cancelled = true; };
  }, [bootChecked, isAuthenticated]);

  useEffect(() => {
    pollSeqRef.current += 1;
    hasLoadedOnceRef.current = false;
    setMessagesReady(false);
    pollingChannelIdRef.current = selectedChannel?.id ?? null;
    setMessages([]);
  }, [selectedChannel?.id]);

  /* ----------------------------- Poll messages for active channel (initial load + ongoing updates) ----------------------------- */
  useAdaptivePolling(
  async () => {
    if (!selectedChannel?.id) return;

    const channelId = selectedChannel.id;
    const seq = ++pollSeqRef.current;

    try {
      const [msgRes, channelRes] = await Promise.all([
        getChannelMessages(channelId),
        getChannels(),
      ]);
      if (seq !== pollSeqRef.current) return;
      if (channelId !== pollingChannelIdRef.current) return;

      const raw = msgRes?.data;
      const list = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.results)
          ? raw.results
          : [];

      setMessages((prev) => {
        const serverIds = new Set(list.map((m) => m.id));
        const nextMax = list.reduce((max, m) => Math.max(max, Number(m.id) || 0), 0);
        const extras = prev.filter((m) => !serverIds.has(m.id) && Number(m.id) > nextMax);
        const next = extras.length ? [...list, ...extras].sort((a, b) => a.id - b.id) : list;
        if (prev.length === next.length && prev.every((m, i) => m.id === next[i].id)) {
          return prev;
        }
        return next;
      });
      hasLoadedOnceRef.current = true;
      setMessagesReady(true);
      if (channelRes?.data?.length) setChannels(channelRes.data);
    } catch (err) {
      hasLoadedOnceRef.current = true;
      setMessagesReady(true);
      const detail = getApiErrorDetail(err, "Polling failed.");
      console.error("[TeamChat] polling failed", {
        channelId,
        status: err?.response?.status,
        detail,
        data: err?.response?.data,
      });
    }
  },
  {
    enabled:
      bootChecked &&
      isAuthenticated &&
      Boolean(selectedChannel?.id),

    fastMs: 2000,   // visible + active
    slowMs: 10000,  // hidden or idle
    idleMs: 60000,
  }
);


  /* ----------------------------- Members (team-chat-only endpoint: org-wide, no role filter) ----------------------------- */
  useEffect(() => {
    getTeamChatMembers()
      .then((res) => {
        const list = res?.data ?? [];
        setMembers(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        setMembers([]);
        const detail = getApiErrorDetail(err, "Unable to load team members.");
        console.error("[TeamChat] members load failed", {
          status: err?.response?.status,
          detail,
          data: err?.response?.data,
        });
        toast.error(`Team Chat members failed to load: ${detail}`);
      });
  }, []);

  const getMentionCandidates = () => {
    const memberList = members.map((m) => ({
      id: m.id,
      username: m.username || "",
      displayName: getDisplayName(m) || m.username || "",
    })).filter((m) => m.username);
    return [
      { id: "everyone", username: "everyone", displayName: "everyone" },
      ...memberList,
    ];
  };

  const replyToMessage = (msg) => {
    const mentionUser = msg.sender_username ?? msg.sender_display ?? msg.user ?? "";
    const replyUser = msg.sender_display ?? msg.sender_username ?? msg.user ?? "";
    setInput((prev) => prev + `@${mentionUser} `);
    setReplyContext({
      message_id: msg.id,
      user: replyUser,
      time: msg.time ?? formatMessageTime(msg.created_at),
      text: msg.body ?? msg.text ?? "",
    });
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const renderMessageWithMentions = (text) => {
  // Matches @username-style mentions or @everyone
  const parts = text.split(
  /(@(?:everyone|[A-Za-z0-9_.-]+))(?![A-Za-z0-9_.-])/g
);


  return parts.map((part, idx) => {
    if (part.startsWith("@")) {
      const isEveryone = part.toLowerCase() === "@everyone";

      return (
        <Box
          key={idx}
          component="span"
          sx={{
            px: 0.5,
            borderRadius: 0.6,
            bgcolor: isEveryone ? "primary.main" : "#f6e7a2",
            color: isEveryone ? "primary.contrastText" : "#1d1c1d",
            fontWeight: 700,
            mr: 0.5,
            display: "inline-block",
          }}
        >
          {part}
        </Box>
      );
    }

    return <span key={idx}>{part}</span>;
  });
};


  const mentionIdsFromText = (text) => {
    const ids = [];
    const byUsername = new Map(
      members.map((m) => [String(m.username || "").toLowerCase(), m.id])
    );
    const re = /@([A-Za-z0-9_.-]+)/g;
    let match;
    while ((match = re.exec(text || ""))) {
      const token = match[1].toLowerCase();
      if (token === "everyone" || token === "all") {
        members.forEach((m) => ids.push(m.id));
        continue;
      }
      const id = byUsername.get(token);
      if (id) ids.push(id);
    }
    return [...new Set(ids)];
  };

  const sendMessage = async () => {
    const body = input.trim();
    if (!body || !selectedChannel?.id) return;
    setSending(true);
    try {
      const payload = {
        body,
        mentions: [...new Set([...(mentionIds || []), ...mentionIdsFromText(body)])],
        ...(replyContext
          ? {
              reply_to: {
                message_id: replyContext.message_id,
                user: replyContext.user,
                time: replyContext.time,
                text: replyContext.text,
              },
            }
          : {}),
      };
      const res = await sendChannelMessage(selectedChannel.id, payload);
      const created = res?.data;
      if (created) {
        setMessages((prev) => [...prev, created]);
        const notified = created.notified_user_ids || created.notified_user_ids || [];
        const mentioned = payload.mentions || [];
        if (mentioned.length > 0 && notified.length === 0) {
          toast.warning(
            "Message sent, but nobody was notified. @ another user who enabled notifications (or @ yourself to test this phone)."
          );
        }
        if (res?.data?.was_filtered) {
          toast.warning("Your message was filtered for inappropriate language.", {
            position: "top-center",
            autoClose: 4000,
            theme: "colored",
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
      }
      setInput("");
      setMentionIds([]);
      setReplyContext(null);
      setShowMentions(false);
      setShowQuickPanel(false);
    } catch (err) {
      const detail = getApiErrorDetail(err, "Unable to send message.");
      console.error("[TeamChat] send failed", {
        channelId: selectedChannel?.id,
        status: err?.response?.status,
        detail,
        data: err?.response?.data,
        mentionIds,
      });
      toast.error(`Message send failed: ${detail}`);
    } finally {
      setSending(false);
    }
  };

  const selectMention = (candidate) => {
    if (candidate.id === "everyone") {
      setMentionIds(members.map((m) => m.id));
      setInput((prev) => prev.replace(/@[A-Za-z0-9_.-]*$/, "@everyone "));
    } else {
      setMentionIds((prev) => (prev.includes(candidate.id) ? prev : [...prev, candidate.id]));
      setInput((prev) => prev.replace(/@[A-Za-z0-9_.-]*$/, `@${candidate.username} `));
    }
    setShowMentions(false);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  useEffect(() => {
    const scrollEl = messagesScrollRef.current;
    if (!scrollEl) return;

    scrollEl.scrollTo({
      top: scrollEl.scrollHeight,
      behavior: "smooth",
    });
  }, [messages]);

  /* ----------------------------- Last read per channel (for unread dot) ----------------------------- */
  useEffect(() => {
    if (!selectedChannel?.id || messages.length === 0) return;
    const maxId = Math.max(
      0,
      ...messages.map((m) => Number(m.id) || 0),
      Number(selectedChannel.last_message_id) || 0
    );
    if (!maxId) return;
    const key = String(selectedChannel.id);
    setChannels((prev) =>
      prev.map((ch) =>
        String(ch.id) === key && (Number(ch.last_message_id) || 0) < maxId
          ? { ...ch, last_message_id: maxId }
          : ch
      )
    );
    setLastReadByChannel((prev) => {
      const current = Math.max(Number(prev[key]) || 0, Number(prev[selectedChannel.id]) || 0);
      if (current >= maxId) return prev;
      const next = { ...prev, [key]: maxId };
      try {
        localStorage.setItem("teamchat_last_read", JSON.stringify(next));
      } catch {}
      return next;
    });
  }, [selectedChannel?.id, messages]);

  const grouped = classifyTeamChatChannels(channels);
  const orgName =
    currentUser?.organization_name ||
    currentUser?.organization?.name ||
    currentUser?.company_name ||
    "";
  const searchQ = homeSearch.trim().toLowerCase();
  const matchSearch = (name) =>
    !searchQ || String(name || "").toLowerCase().includes(searchQ);
  const showMobileChat = Boolean(isMobile && mobileShowChat && selectedChannel);

  const openChannel = (channel) => {
    if (!channel?.id) {
      toast.info("That channel is not available yet.");
      return;
    }
    setSelectedChannel(channel);
    setMobileShowChat(true);
    setDrawerOpen(false);
  };

  const mentionMember = (m) => {
    setMentionIds((prev) => (prev.includes(m.id) ? prev : [...prev, m.id]));
    setInput((prev) => `${prev.replace(/@[A-Za-z0-9_.-]*$/, "")}@${m.username} `);
    if (!selectedChannel && grouped.orgChannels[0]) {
      setSelectedChannel(grouped.orgChannels[0]);
      setMobileShowChat(true);
    } else if (selectedChannel) {
      setMobileShowChat(true);
    }
    setDrawerOpen(false);
  };

  /* ----------------------------- SIDE PANEL ----------------------------- */

  const channelNav = (
    <Box sx={{ p: 0.5 }}>
      {loadingChannels ? (
        <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          <SectionLabel>CHANNELS</SectionLabel>
          {grouped.orgChannels.filter((c) => matchSearch(c.name) || matchSearch(c.slug)).map((c) => (
            <NavRow
              key={c.id}
              icon={<TagRoundedIcon />}
              label={c.name || c.slug}
              locked={String(c.slug || "").toLowerCase() === "support"}
              selected={!isMobile && selectedChannel?.id === c.id}
              unread={channelIsUnread(c, lastReadByChannel, selectedChannel?.id)}
              size={isMobile ? "comfortable" : "default"}
              onClick={() => openChannel(c)}
            />
          ))}
          {grouped.territoryChannels.length > 0 && (
            <>
              <Box sx={{ height: 1, bgcolor: "divider", my: 1.25 }} />
              <SectionLabel>TERRITORIES</SectionLabel>
              {grouped.territoryChannels.filter((c) => matchSearch(c.name) || matchSearch(c.slug)).map((c) => (
                <NavRow
                  key={c.id}
                  icon={<LocationOnRoundedIcon />}
                  label={c.name || c.slug}
                  selected={selectedChannel?.id === c.id}
                  unread={channelIsUnread(c, lastReadByChannel, selectedChannel?.id)}
                  size={isMobile ? "comfortable" : "default"}
                  onClick={() => openChannel(c)}
                />
              ))}
            </>
          )}
        </>
      )}
    </Box>
  );

  const memberNav = (
    <Box sx={{ px: 0.5 }}>
      {members
        .filter((m) => m.is_active !== false)
        .filter((m) => matchSearch(getDisplayName(m)) || matchSearch(m.username))
        .map((m) => {
          const name = getDisplayName(m) || m.username || "";
          return (
            <PersonRow
              key={m.id}
              member={{ name, username: m.username, role: m.role }}
              initials={getUserInitials(
                { first_name: m.first_name, last_name: m.last_name, full_name: name, username: m.username },
                "U"
              )}
              online={isAvailable(m)}
              onClick={() => mentionMember(m)}
            />
          );
        })}
    </Box>
  );

  const searchField = (
    <TextField
      size="small"
      fullWidth
      placeholder="Search channels, people..."
      value={homeSearch}
      onChange={(e) => setHomeSearch(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchRoundedIcon sx={{ color: "text.secondary", fontSize: 19 }} />
          </InputAdornment>
        ),
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: 1.5,
          bgcolor: "action.hover",
          "& fieldset": { borderColor: "transparent" },
        },
      }}
    />
  );

  const workspaceHeader = (
    <Box
      sx={{
        height: 72,
        px: 2,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.5,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 900,
          fontSize: 15,
        }}
      >
        TC
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography fontSize={14} fontWeight={800} noWrap>
          Team Chat
        </Typography>
        <Typography fontSize={11.5} color="text.secondary" noWrap>
          {orgName || " "}
        </Typography>
      </Box>
    </Box>
  );

  const userFooter = (
    <Box sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1.2, flexShrink: 0 }}>
      <Badge
        overlap="circular"
        variant="dot"
        color="success"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Avatar
          sx={{
            width: 34,
            height: 34,
            bgcolor: "primary.main",
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          {getUserInitials(currentUser, "U")}
        </Avatar>
      </Badge>
      <Box sx={{ minWidth: 0 }}>
        <Typography fontSize={13} fontWeight={700} noWrap>
          {getDisplayName(currentUser) || currentUser?.username || "You"}
        </Typography>
        <Typography fontSize={11} color="text.secondary" noWrap>
          {currentUser?.role || "Member"}
        </Typography>
      </Box>
    </Box>
  );

  const LeftPanelContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: "background.paper", minHeight: 0 }}>
      {!isMobile && (
        <>
          {workspaceHeader}
          <Divider />
        </>
      )}
      {!isMobile && <Box sx={{ p: 2, pb: 1, flexShrink: 0 }}>{searchField}</Box>}
      <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0, px: isMobile ? 1.25 : 1, pt: isMobile ? 1.5 : 0 }}>
        {channelNav}
        <Divider sx={{ my: isMobile ? 1.5 : 1 }} />
        {isMobile ? (
          <>
            <SectionLabel>DIRECT MESSAGE</SectionLabel>
            <ListItemButton
              onClick={(e) => setTeammatePickerAnchor(e.currentTarget)}
              sx={{
                minHeight: 48,
                px: 1.25,
                borderRadius: 1.5,
                mb: 0.5,
              }}
            >
              <AddRoundedIcon sx={{ mr: 1.1, fontSize: 22, color: "primary.main" }} />
              <Typography sx={{ fontSize: 16.5, fontWeight: 600 }}>
                Message a Teammate
              </Typography>
            </ListItemButton>
          </>
        ) : (
          <>
            <SectionLabel>MEMBERS</SectionLabel>
            {memberNav}
          </>
        )}
      </Box>
      {!isMobile && (
        <>
          <Divider />
          {userFooter}
        </>
      )}
    </Box>
  );

  /* ----------------------------- RENDER ----------------------------- */

  return (
    <Box
      sx={{
        height: "100%",
        maxHeight: "100%",
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        p: 0,
        bgcolor: (theme) => ({
          xs: theme.palette.mode === "dark" ? theme.palette.background.default : CHAT_BG,
          md: "transparent",
        }),
        position: "relative",
      }}
    >
      {isMobile && !showMobileChat && (
        <Box
          sx={{
            height: "100%",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.paper",
          }}
        >
          <Box
            sx={{
              height: 72,
              px: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              bgcolor: "background.paper",
              borderBottom: "1px solid",
              borderColor: "divider",
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "11px",
                bgcolor: "primary.main",
                color: "#fff",
                display: "grid",
                placeItems: "center",
                fontWeight: 800,
              }}
            >
              TC
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={800} fontSize={17}>
                Team Chat
              </Typography>
              <Typography fontSize={12} color="text.secondary" noWrap>
                {orgName || " "}
              </Typography>
            </Box>
            <Tooltip title="Enable browser notifications">
              <span>
                <IconButton
                  size="small"
                  disabled={pushBusy}
                  onClick={async () => {
                    setPushBusy(true);
                    try {
                      await enablePushFromUserTap();
                    } finally {
                      setPushBusy(false);
                    }
                  }}
                  aria-label="Enable notifications"
                >
                  <NotificationsNoneRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Box sx={{ flex: 1, overflowY: "auto", bgcolor: "background.paper", minHeight: 0 }}>{LeftPanelContent}</Box>
        </Box>
      )}
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          height: "100%",
          maxHeight: "100%",
          minHeight: 0,
          borderRadius: { xs: 0, md: 2.5 },
          display: "flex",
          flexDirection: "row",
          overflow: "hidden",
          border: { xs: "none", md: "1px solid" },
          borderColor: "divider",
          bgcolor: "background.paper",
          display: isMobile && !showMobileChat ? "none" : "flex",
        }}
      >
        {!isMobile && (
          <Box
            sx={{
              width: 280,
              flexShrink: 0,
              minHeight: 0,
              height: "100%",
              borderRight: "1px solid",
              borderColor: "divider",
            }}
            data-tour="team-chat-channels"
          >
            {LeftPanelContent}
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              minHeight: isMobile ? 52 : 56,
              px: isMobile ? 1 : 2,
              py: isMobile ? 0.75 : 1,
              borderBottom: isMobile ? "none" : "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              gap: isMobile ? 0.5 : 1.5,
            }}
          >
            {isMobile && (
              <IconButton
                size="small"
                onClick={() => setMobileShowChat(false)}
                data-tour="team-chat-channels"
                sx={{ color: "text.primary" }}
              >
                <ArrowBackIosNewRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
            {isMobile ? (
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  mx: 0.5,
                  px: 1.5,
                  py: 0.7,
                  borderRadius: 8,
                  bgcolor: CHAT_BLUE,
                  backgroundImage:
                    "linear-gradient(180deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.06) 55%, rgba(0, 40, 80, 0.12) 100%)",
                  backdropFilter: "blur(14px)",
                  WebkitBackdropFilter: "blur(14px)",
                  border: "1px solid rgba(255,255,255,0.38)",
                  boxShadow: "0 8px 22px rgba(0, 87, 164, 0.32)",
                  color: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ fontSize: 14.5, fontWeight: 800, lineHeight: 1.2 }} noWrap>
                  #{selectedChannel?.slug || selectedChannel?.name || "chat"}
                </Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 500, color: "rgba(255,255,255,0.82)", mt: 0.15 }} noWrap>
                  {members.filter((m) => m.is_active !== false).length} member
                  {members.filter((m) => m.is_active !== false).length === 1 ? "" : "s"}
                  {String(selectedChannel?.slug || "").toLowerCase() === "support" ? " • private" : ""}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontSize: 15, fontWeight: 800 }} noWrap>
                    #{selectedChannel?.slug ?? selectedChannel?.name ?? "—"}
                  </Typography>
                  {String(selectedChannel?.slug || "").toLowerCase() === "support" && (
                    <LockRoundedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                  )}
                </Stack>
                {selectedChannel?.slug && CHANNEL_DESCRIPTIONS[selectedChannel.slug] && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.25, fontWeight: 400, fontSize: 12, lineHeight: 1.4 }}
                  >
                    {CHANNEL_DESCRIPTIONS[selectedChannel.slug]}
                  </Typography>
                )}
              </Box>
            )}
            <Tooltip title="Enable browser notifications">
              <span>
                <IconButton
                  size="small"
                  disabled={pushBusy}
                  onClick={async () => {
                    setPushBusy(true);
                    try {
                      await enablePushFromUserTap();
                    } finally {
                      setPushBusy(false);
                    }
                  }}
                  aria-label="Enable notifications"
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: isMobile ? "50%" : 1.25,
                    width: isMobile ? 36 : undefined,
                    height: isMobile ? 36 : undefined,
                    bgcolor: isMobile ? "background.paper" : undefined,
                  }}
                >
                  <NotificationsNoneRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Box
            ref={messagesScrollRef}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overscrollBehavior: "contain",
              WebkitOverflowScrolling: "touch",
              px: { xs: isMobile ? 1.5 : 2, md: 3 },
              py: isMobile ? 1.25 : 2,
              bgcolor: "background.paper",
            }}
          >
            {!selectedChannel ? (
              <Typography color="text.secondary">Select a channel</Typography>
            ) : (
              <>
              <Box sx={{ px: isMobile ? 0.5 : 0, pt: isMobile ? 1 : 0.5, pb: 2.5 }}>
                <Typography sx={{ fontSize: isMobile ? 26 : 22, fontWeight: 800, letterSpacing: -0.4, mb: 0.75 }}>
                  Welcome!
                </Typography>
                {CHANNEL_DESCRIPTIONS[String(selectedChannel?.slug || "").toLowerCase()] ? (
                  <Typography sx={{ fontSize: isMobile ? 14.5 : 14, color: "text.secondary", lineHeight: 1.5 }}>
                    <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                      {CHANNEL_TITLES[String(selectedChannel.slug).toLowerCase()] || selectedChannel.name}
                    </Box>
                    {" — "}
                    {CHANNEL_DESCRIPTIONS[String(selectedChannel.slug).toLowerCase()]}
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: isMobile ? 14.5 : 14, color: "text.secondary", lineHeight: 1.45 }}>
                    This is the start of #{selectedChannel?.slug ?? selectedChannel?.name}. Messages stay in this channel.
                  </Typography>
                )}
              </Box>
              {!messagesReady && (
                <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {messages.length === 0 && !isMobile ? (
                <Typography color="text.secondary">No messages yet</Typography>
              ) : messages.length > 0 ? (
              <>
              <Typography
                sx={{
                  textAlign: "center",
                  color: "text.secondary",
                  fontSize: isMobile ? 12 : 10.5,
                  fontWeight: isMobile ? 600 : 400,
                  mb: isMobile ? 2 : 3,
                }}
              >
                Today
              </Typography>
              {messages.map((msg) => {
                const mine = Number(currentUser?.id) === Number(msg.sender);
                const senderName = msg.sender_display ?? msg.sender_username ?? (mine ? "You" : "—");
                if (isMobile) {
                  return (
                    <Box key={msg.id} sx={{ display: "flex", gap: 1.25, mb: 2, px: 0.25 }}>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          mt: 0.15,
                          bgcolor: "primary.main",
                          fontSize: 13,
                          fontWeight: 800,
                        }}
                      >
                        {getUserInitials(
                          { full_name: senderName, username: msg.sender_username },
                          "?"
                        )}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="baseline">
                          <Typography sx={{ fontSize: 15, fontWeight: 800, lineHeight: 1.2 }}>
                            {senderName}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                            {formatMessageTime(msg.created_at)}
                          </Typography>
                        </Stack>
                        {(msg.replyTo ?? msg.reply_to) && (
                          <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.35 }}>
                            Replying to {(msg.replyTo ?? msg.reply_to).user || "a message"}
                          </Typography>
                        )}
                        <Typography component="div" sx={{ fontSize: 15, lineHeight: 1.45, mt: 0.2 }}>
                          {renderMessageWithMentions(msg.body ?? msg.text ?? "")}
                        </Typography>
                        <Stack direction="row" spacing={1.5} sx={{ mt: 0.45 }}>
                          <Typography
                            fontSize={12}
                            color="text.secondary"
                            sx={{ cursor: "pointer" }}
                            onClick={() => replyToMessage(msg)}
                          >
                            Reply
                          </Typography>
                          {mine && (
                            <Typography
                              fontSize={12}
                              color="error.main"
                              sx={{ cursor: "pointer" }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!selectedChannel?.id) return;
                                setMessageToDelete(msg);
                                setConfirmDeletePost(true);
                              }}
                            >
                              Delete
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </Box>
                  );
                }
                return (
                <Box
                  key={msg.id}
                  sx={{
                    mb: 2.4,
                    display: "flex",
                    justifyContent: mine ? "flex-end" : "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: "78%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: mine ? "flex-end" : "flex-start",
                    }}
                  >
                    <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.5, px: 0.25 }}>
                      <Typography sx={{ fontSize: 10.5, fontWeight: 750 }}>
                        {senderName}
                      </Typography>
                      <Typography sx={{ fontSize: 9.8, color: "text.secondary" }}>
                        {formatMessageTime(msg.created_at)}
                      </Typography>
                    </Stack>

                    {(msg.replyTo ?? msg.reply_to) && (() => {
                      const replyData = msg.replyTo ?? msg.reply_to;
                      return (
                        <Box
                          sx={{
                            mb: 0.6,
                            p: 1,
                            borderRadius: 1.5,
                            bgcolor: "action.hover",
                            maxWidth: "100%",
                          }}
                        >
                          <Typography fontSize={11} fontWeight={700}>
                            Reply
                          </Typography>
                          <Typography fontSize={12} fontWeight={600}>
                            {replyData.user ?? ""}
                          </Typography>
                          <Typography fontSize={12}>{replyData.text ?? ""}</Typography>
                        </Box>
                      );
                    })()}

                    <Paper
                      elevation={0}
                      sx={{
                        px: 1.7,
                        py: 1.25,
                        borderRadius: mine ? "14px 5px 14px 14px" : "5px 14px 14px 14px",
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "#eceff1",
                        color: "text.primary",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography component="div" sx={{ fontSize: 13, lineHeight: 1.55 }}>
                        {renderMessageWithMentions(msg.body ?? msg.text ?? "")}
                      </Typography>
                    </Paper>

                    <Stack direction="row" spacing={1.2} sx={{ mt: 0.6, px: 0.3 }}>
                      <Typography
                        fontSize={10.5}
                        color="text.secondary"
                        sx={{ cursor: "pointer" }}
                        onClick={() => replyToMessage(msg)}
                      >
                        Reply
                      </Typography>
                      {mine && (
                        <Typography
                          fontSize={10.5}
                          color="error.main"
                          sx={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!selectedChannel?.id) return;
                            setMessageToDelete(msg);
                            setConfirmDeletePost(true);
                          }}
                        >
                          Delete
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Box>
                );
              })}
              </>
              ) : null}
              </>
            )}
            <div ref={bottomRef} />
          </Box>

          <Box
            sx={{
              px: isMobile ? 1.25 : 2,
              py: isMobile ? 1 : 1.25,
              borderTop: isMobile ? "none" : "1px solid",
              borderColor: "divider",
              position: "relative",
              flexShrink: 0,
              bgcolor: "background.paper",
            }}
          >
            {/* --------------------------------------------------------------------------------------------Replying to chip */}
            {replyContext && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 0.75,
                  px: 1.5,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                  borderLeft: "3px solid",
                  borderColor: "primary.main",
                }}
              >
                <Typography fontSize={12} color="text.secondary">
                  Replying to <strong>{replyContext.user}</strong>: &quot;{(replyContext.text || "").slice(0, 60)}{(replyContext.text || "").length > 60 ? "…" : ""}&quot;
                </Typography>
                <Typography
                  component="button"
                  type="button"
                  fontSize={12}
                  fontWeight={600}
                  color="primary.main"
                  onClick={() => setReplyContext(null)}
                  sx={{
                    ml: "auto",
                    border: 0,
                    background: "none",
                    cursor: "pointer",
                    padding: 0,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Cancel
                </Typography>
              </Box>
            )}
            {String(selectedChannel?.slug || "").toLowerCase() === "support" && !isMobile && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.8, px: 0.5 }}>
                <LockRoundedIcon sx={{ fontSize: 12, color: "text.secondary" }} />
                <Typography sx={{ fontSize: 10.5, color: "text.secondary" }}>
                  Support is scoped by the poster’s territory
                </Typography>
              </Stack>
            )}
            <Paper
              variant="outlined"
              sx={{
                borderRadius: isMobile ? "22px" : 1.8,
                minHeight: isMobile ? 40 : undefined,
                px: isMobile ? 0.5 : 0.8,
                py: isMobile ? 0 : 0.8,
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                position: "relative",
                borderColor: (theme) =>
                  isMobile && theme.palette.mode === "light" ? "#d0d2d3" : "divider",
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "background.paper",
              }}
            >
            <IconButton
              size="small"
              onClick={() => inputRef.current?.focus()}
              aria-label="Add"
              sx={{
                width: isMobile ? 36 : undefined,
                height: isMobile ? 36 : undefined,
                p: isMobile ? 0 : undefined,
                alignSelf: "center",
              }}
            >
              <AddRoundedIcon fontSize="small" />
            </IconButton>
            {!isMobile && (
            <Tooltip title="Attachments coming soon">
              <span>
                <IconButton size="small" disabled aria-label="Attach">
                  <AttachFileRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            )}
            <Box sx={{ position: "relative", display: isMobile ? "none" : "block" }}>
              <IconButton size="small" onClick={() => setShowQuickPanel((v) => !v)}>
                <EmojiEmotionsOutlinedIcon fontSize="small" />
              </IconButton>

              {showQuickPanel && (
                <ClickAwayListener onClickAway={() => setShowQuickPanel(false)}>
                  <Paper
                    sx={{
                      position: "absolute",
                      bottom: "100%",
                      left: 0,
                      mb: 1,
                      width: 260,
                      borderRadius: 2,
                      p: 1.5,
                      zIndex: 30,
                    }}
                  >
                    <Typography fontSize={12} fontWeight={700}>
                      Emojis
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                      {EMOJIS.map((e) => (
                        <Box
                          key={e}
                          onClick={() => setInput((prev) => prev + e)}
                          sx={{ fontSize: 22, cursor: "pointer" }}
                        >
                          {e}
                        </Box>
                      ))}
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Typography fontSize={12} fontWeight={700}>
                      Quick replies
                    </Typography>
                    {QUICK_REPLIES.map((q) => (
                      <Box
                        key={q}
                        onClick={() => setInput(q)}
                        sx={{
                          px: 1,
                          py: 0.75,
                          borderRadius: 1,
                          cursor: "pointer",
                          fontSize: 13,
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        {q}
                      </Box>
                    ))}
                  </Paper>
                </ClickAwayListener>
              )}
            </Box>

            {/* ------------------------------------------------------------@ MENTIONS */}
            {showMentions && (
              <Paper
                sx={{
                  position: "absolute",
                  bottom: "100%",
                  left: 48,
                  mb: 1,
                  minWidth: 200,
                  maxWidth: isMobile ? "calc(100vw - 32px)" : 320,
                  maxHeight: isMobile ? "35vh" : 320,
                  overflowY: "auto",
                  borderRadius: 2,
                  zIndex: 25,
                }}
              >
                {getMentionCandidates()
                  .filter((m) =>
                    m.username.toLowerCase().includes(mentionQuery.toLowerCase()) ||
                    m.displayName.toLowerCase().includes(mentionQuery.toLowerCase())
                  )
                  .map((m) => (
                    <Box
                      key={m.id}
                      onClick={() => selectMention(m)}
                      sx={{
                        px: 2,
                        py: 1,
                        cursor: "pointer",
                        fontSize: 14,
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      @{m.username}
                    </Box>
                  ))}
              </Paper>
            )}

            <TextField
              inputRef={inputRef}
              fullWidth
              multiline
              rows={1}
              maxRows={isMobile ? 1 : 4}
              variant="standard"
              inputProps={{ "data-tour": "team-chat-quick-mention" }}
              placeholder={
                selectedChannel
                  ? `Message #${selectedChannel.slug ?? selectedChannel.name}`
                  : "Select a channel"
              }
              value={input}
              onChange={(e) => {
                const value = e.target.value;
                setInput(value);
                const match = value.match(/@([A-Za-z0-9_.-]*)$/);
                if (match) {
                  setMentionQuery(match[1]);
                  setShowMentions(true);
                } else {
                  setShowMentions(false);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              InputProps={{ disableUnderline: true }}
              sx={{
                flex: 1,
                "& .MuiInputBase-root": {
                  alignItems: "center",
                  p: 0,
                  minHeight: isMobile ? 40 : undefined,
                },
                "& textarea": {
                  fontSize: isMobile ? 16 : 13,
                  lineHeight: 1.3,
                  py: isMobile ? "8px" : 0,
                  px: isMobile ? "2px" : 0,
                  maxHeight: isMobile ? 36 : undefined,
                  overflow: "hidden",
                },
              }}
            />

            <IconButton
              type="button"
              color="primary"
              disabled={sending || !input.trim()}
              onPointerDown={(e) => {
                e.preventDefault();
                sendMessage();
              }}
              sx={{
                mb: isMobile ? 0 : 0.1,
                width: isMobile ? 36 : undefined,
                height: isMobile ? 36 : undefined,
                p: isMobile ? 0 : undefined,
                alignSelf: "center",
              }}
            >
              {sending ? <CircularProgress size={18} /> : <SendRoundedIcon />}
            </IconButton>
            </Paper>
          </Box>
        </Box>
      </Paper>

      <Popover
        open={Boolean(teammatePickerAnchor)}
        anchorEl={teammatePickerAnchor}
        onClose={() => setTeammatePickerAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              ml: 1,
              width: "min(320px, calc(100vw - 32px))",
              maxHeight: 360,
              borderRadius: 2.5,
              boxShadow: "0 12px 40px rgba(15, 23, 42, 0.16)",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "text.secondary" }}>
            TEAMMATES
          </Typography>
        </Box>
        <List dense disablePadding sx={{ pb: 0.75 }}>
          {members
            .filter((m) => m.is_active !== false)
            .filter((m) => Number(m.id) !== Number(currentUser?.id))
            .map((m) => {
              const name = getDisplayName(m) || m.username || "Teammate";
              return (
                <ListItemButton
                  key={m.id}
                  onClick={() => {
                    setTeammatePickerAnchor(null);
                    toast.info("Direct messages are coming soon. You can @ them in a channel.");
                  }}
                  sx={{ px: 2, py: 0.85 }}
                >
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, bgcolor: "primary.main" }}>
                      {getUserInitials(
                        { first_name: m.first_name, last_name: m.last_name, full_name: name, username: m.username },
                        "U"
                      )}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={name}
                    secondary={m.username ? `@${m.username}` : m.role || ""}
                    primaryTypographyProps={{
                      fontSize: 13,
                      fontWeight: 600,
                      noWrap: true,
                      sx: { fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif' },
                    }}
                    secondaryTypographyProps={{
                      fontSize: 11.5,
                      noWrap: true,
                    }}
                  />
                </ListItemButton>
              );
            })}
          {members.filter((m) => m.is_active !== false && Number(m.id) !== Number(currentUser?.id)).length === 0 && (
            <Typography sx={{ px: 2, py: 1.5, fontSize: 13, color: "text.secondary" }}>
              No teammates to message yet.
            </Typography>
          )}
        </List>
      </Popover>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            top: APP_HEADER_HEIGHT,
            height: `calc(100vh - ${APP_HEADER_HEIGHT}px)`,
            width: 300,
          },
        }}
      >
        {LeftPanelContent}
      </Drawer>

      <ConfirmDialog
        open={confirmDeletePost}
        onClose={() => {
          setConfirmDeletePost(false);
          setMessageToDelete(null);
        }}
        onConfirm={async () => {
          if (!messageToDelete || !selectedChannel?.id) return;
          try {
            await deleteChannelMessage(selectedChannel.id, messageToDelete.id);
            setMessages((prev) => prev.filter((m) => m.id !== messageToDelete.id));
          } catch (err) {
            const detail = getApiErrorDetail(err, "Unable to delete message.");
            console.error("[TeamChat] delete failed", {
              channelId: selectedChannel?.id,
              messageId: messageToDelete?.id,
              status: err?.response?.status,
              detail,
              data: err?.response?.data,
            });
            toast.error(`Delete message failed: ${detail}`);
          } finally {
            setConfirmDeletePost(false);
            setMessageToDelete(null);
          }
        }}
        title="Delete post"
        message="Are you sure you want to delete this message?"
        confirmText="Delete"
        cancelText="Cancel"
        itemName={messageToDelete ? (messageToDelete.body ?? messageToDelete.text ?? "").slice(0, 50) : null}
        severity="error"
      />
    </Box>
  );
}
